import { useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { ChevronLeft, Settings } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Link } from "wouter";

const settingsFormSchema = z.object({
  accrualStartMonth: z.number().min(1).max(12).default(1),
  accrualStartDay: z.number().min(1).max(31).default(1),
  allowOverlappingRequests: z.boolean().default(false),
  requireReasonForDenial: z.boolean().default(true),
  showBalanceToEmployees: z.boolean().default(true),
  allowEmployeeCancellation: z.boolean().default(true),
  cancellationCutoffHours: z.number().min(0).default(24),
  defaultWorkHoursPerDay: z.string().default("8"),
  notifyManagerOnRequest: z.boolean().default(true),
  notifyEmployeeOnApproval: z.boolean().default(true),
  notifyHrOnAllRequests: z.boolean().default(false),
});

type SettingsFormData = z.infer<typeof settingsFormSchema>;

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

export default function TimeOffSettings() {
  const { data: settings, isLoading } = useQuery<any>({
    queryKey: ["/api/time-off/settings"],
  });

  const form = useForm<SettingsFormData>({
    resolver: zodResolver(settingsFormSchema),
    defaultValues: {
      accrualStartMonth: 1,
      accrualStartDay: 1,
      allowOverlappingRequests: false,
      requireReasonForDenial: true,
      showBalanceToEmployees: true,
      allowEmployeeCancellation: true,
      cancellationCutoffHours: 24,
      defaultWorkHoursPerDay: "8",
      notifyManagerOnRequest: true,
      notifyEmployeeOnApproval: true,
      notifyHrOnAllRequests: false,
    },
  });

  useEffect(() => {
    if (settings) {
      form.reset({
        accrualStartMonth: settings.accrualStartMonth || 1,
        accrualStartDay: settings.accrualStartDay || 1,
        allowOverlappingRequests: settings.allowOverlappingRequests || false,
        requireReasonForDenial: settings.requireReasonForDenial ?? true,
        showBalanceToEmployees: settings.showBalanceToEmployees ?? true,
        allowEmployeeCancellation: settings.allowEmployeeCancellation ?? true,
        cancellationCutoffHours: settings.cancellationCutoffHours || 24,
        defaultWorkHoursPerDay: settings.defaultWorkHoursPerDay || "8",
        notifyManagerOnRequest: settings.notifyManagerOnRequest ?? true,
        notifyEmployeeOnApproval: settings.notifyEmployeeOnApproval ?? true,
        notifyHrOnAllRequests: settings.notifyHrOnAllRequests || false,
      });
    }
  }, [settings, form]);

  const saveMutation = useMutation({
    mutationFn: async (data: SettingsFormData) => {
      return apiRequest("PUT", "/api/time-off/settings", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/settings"] });
      toast({ title: "Settings saved", description: "Time off settings have been updated." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save settings.", variant: "destructive" });
    },
  });

  const onSubmit = (data: SettingsFormData) => {
    saveMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Link href="/time-off">
          <Button variant="ghost" size="sm">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Time Off Settings</h1>
          <p className="text-gray-500">Configure global settings for time off management</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Accrual Settings</CardTitle>
              <CardDescription>Configure when time off accruals start</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="accrualStartMonth"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fiscal Year Start Month</FormLabel>
                      <Select
                        value={field.value.toString()}
                        onValueChange={(v) => field.onChange(parseInt(v))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {MONTHS.map((month) => (
                            <SelectItem key={month.value} value={month.value.toString()}>
                              {month.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="accrualStartDay"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fiscal Year Start Day</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          max={31}
                          value={field.value}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="defaultWorkHoursPerDay"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Work Hours Per Day</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.5"
                        min={1}
                        max={24}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Used to calculate hours for full-day requests
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Request Settings</CardTitle>
              <CardDescription>Configure how time off requests are handled</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="allowOverlappingRequests"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <FormLabel>Allow Overlapping Requests</FormLabel>
                      <FormDescription>
                        Allow employees to submit requests for dates they already have approved time off
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="requireReasonForDenial"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <FormLabel>Require Reason for Denial</FormLabel>
                      <FormDescription>
                        Managers must provide a reason when denying requests
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="allowEmployeeCancellation"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <FormLabel>Allow Employee Cancellation</FormLabel>
                      <FormDescription>
                        Employees can cancel their approved time off requests
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              {form.watch("allowEmployeeCancellation") && (
                <FormField
                  control={form.control}
                  name="cancellationCutoffHours"
                  render={({ field }) => (
                    <FormItem className="ml-6">
                      <FormLabel>Cancellation Cutoff (Hours)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          value={field.value}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormDescription>
                        Hours before start date when cancellation is no longer allowed
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Employee Visibility</CardTitle>
              <CardDescription>Configure what employees can see</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="showBalanceToEmployees"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <FormLabel>Show Balances to Employees</FormLabel>
                      <FormDescription>
                        Employees can view their own time off balances
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Notifications</CardTitle>
              <CardDescription>Configure email notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="notifyManagerOnRequest"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <FormLabel>Notify Manager on Request</FormLabel>
                      <FormDescription>
                        Send email to manager when employee submits a request
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notifyEmployeeOnApproval"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <FormLabel>Notify Employee on Approval/Denial</FormLabel>
                      <FormDescription>
                        Send email to employee when their request is approved or denied
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notifyHrOnAllRequests"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <FormLabel>Notify HR on All Requests</FormLabel>
                      <FormDescription>
                        Send a copy of all time off requests to HR
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
